import type { StageViewState } from 'src/app/api/owcc/models';
import type { Mq } from 'src/app/core/config/mq.config';

import { InteriorThreeSixtyPerspective } from './awesome-stage.store.enum';
import { StatePerScreenMode } from './state-per-screen-mode';

export const PANNELLUM_CLASS_SELECTOR_ABOUT_MSG = '.pnlm-about-msg';
export const PANNELLUM_CLASS_SELECTOR_LOAD_BOX = '.pnlm-load-box';

export const INTERIOR_THREE_SIXTY = {
  DEFAULT_YAW: 10,
  DEFAULT_PITCH: -10,
  DEFAULT_HFOV: 100
};

export const AMOUNT_OF_INTERIOR_THREE_SIXTY_IMAGES = 6;

/**
 * Predefined image widths.
 *
 * 👉 Whenever possible use predefined image width instead of defining your own
 * as there is a high chance that those images can be delivered from image cache!
 *
 * E.g. when a image is required which needs to be around 700px chose predefined value 768px and scale on the client.
 */
export const IMAGE_DEFAULT_WIDTH = {
  Mq1And2: 768,
  Mq3And4: 1280,
  Mq5AndAbove: 1920
};

/**
 * Defines the image with per MQ.
 *
 * ℹ️ Partly we have the same resolution for different MQs.
 * Actually we need to find a balance between image requests and user experience / traffic.
 *
 * Let's assume that the user might rotate the mobile device, which might cause a change from mq 3 to 4.
 * => same resolution for e.g. mq 3 and 4.
 *
 * It's less expensive to only request images once (for the user and for the providing service aka BBD).
 */
export type Mq1To4 = Mq.Mq1 | Mq.Mq2 | Mq.Mq3 | Mq.Mq4;

export type Mq5AndAbove = 'default';

export const HIGHRES_IMAGE_WIDTH_MAP: Record<Mq1To4 | Mq5AndAbove, number> = {
  mq1: IMAGE_DEFAULT_WIDTH.Mq1And2,
  mq2: IMAGE_DEFAULT_WIDTH.Mq1And2,
  mq3: IMAGE_DEFAULT_WIDTH.Mq3And4,
  mq4: IMAGE_DEFAULT_WIDTH.Mq3And4,
  default: IMAGE_DEFAULT_WIDTH.Mq5AndAbove
};

/**
 * BBD does not provide interior 360 images larger than 1500x1500 px (BIS).
 */
export const INTERIOR_THREESIXTY_IMAGE_WIDTH_MAP: Record<string, number> = {
  mq1: 768,
  mq2: 768,
  mq3: 1280,
  mq4: 1280,
  default: 1500
};

export const CHECK_EXTERIOR_IMAGE_STATE_INTERVAL = 500;

export const IMAGE_LOADER_SPINNER_DEBOUNCE = 500;

// The order of stage perspectives is as follows: front, left, back, right, up, down i.e counter-clockwise
// But order of images needed for Pannellum is front, right, back, left, up, down i.e clockwise
// See link: https://github.com/mpetroff/pannellum/blob/master/doc/json-config-parameters.md#cubemap
export const INTERIOR_THREE_SIXTY_INDEX_ORDER = [
  InteriorThreeSixtyPerspective.Front,
  InteriorThreeSixtyPerspective.Right,
  InteriorThreeSixtyPerspective.Back,
  InteriorThreeSixtyPerspective.Left,
  InteriorThreeSixtyPerspective.Up,
  InteriorThreeSixtyPerspective.Down
];

export const INTERIOR_TRUNK_PERSPECTIVE = 'BET1';

export const toStatesPerScreenMode = (
  nonFullscreenStageStates: StageViewState[],
  fullscreenStageStates: StageViewState[]
): StatePerScreenMode<StageViewState[]> =>
  new StatePerScreenMode<StageViewState[]>(
    nonFullscreenStageStates || [],
    fullscreenStageStates || nonFullscreenStageStates || []
  );

export const isCurrentlyInFullscreenMode = (document: Document): boolean =>
  !!(
    (document.fullscreenElement && document.fullscreenElement !== null) ||
    (document.webkitFullscreenElement && document.webkitFullscreenElement !== null) ||
    (document.mozFullScreenElement && document.mozFullScreenElement !== null) ||
    (document.msFullscreenElement && document.msFullscreenElement !== null)
  );
